import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col, Alert, Button } from "react-bootstrap";
import { Check, Users, Plus } from "lucide-react";
import {
  fetchClasses,
  createClass,
  deleteClass,
  updateClass,
  activateClassesWithSubjectsAndSections,
  manageClassStatusThunk,
} from "./redux/classSlice";
import { fetchSubjects, handleJoyRideRunning } from "../subjects/redux/subjectSlice";
import { fetchTeachers } from "../teachers/redux/teacherSlice";
import ProposedClassesTab from "./ProposedClassesTab";
import ClassListTab from "./ClassListTab";
import AddClassTab from "./AddClassTab";
import { getMasterDataByUserId } from "../../../pages/masterData/redux/masterDataThunk";
import Joyride, { STATUS } from "react-joyride";
import { useLocation, useNavigate } from "react-router-dom";
import { addClassTourSteps, classSteps } from "../../../tour/timetableTourSteps";
import { createPortal } from "react-dom";
import { setStepIndex, startTour, stopTour } from "../../../redux/globalJoyrideSlice";
import GlobalJoyrideCloseButton from "../../../components/GlobalJoyrideCloseButton";
import "./ClassManagement.css";


const ErrorBoundary = ({ children }) => {
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const errorHandler = (error) => {
      console.error("Error caught by boundary:", error);
      setHasError(true);
    };

    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  if (hasError) {
    return (
      <Alert variant="danger">
        Something went wrong. Please refresh the page.
      </Alert>
    );
  }

  return children;
};

const ClassManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    classes: allClasses,
    loading: classesLoading,
    error: classesError,
  } = useSelector((state) => state.classes);
  const startClassTourRef = useRef(
    location.state?.startClassTour === true
  );

  const startClassTour = startClassTourRef.current;


  const { subjects, loading: subjectsLoading } = useSelector(
    (state) => state.subjects
  );
  const { teachers, loading: teachersLoading } = useSelector(
    (state) => state.teachers
  );
  const { user } = useSelector((state) => state.authSlice);
  const schoolId = useSelector((state) => state.masterData.school_id);

  const [activeTab, setActiveTab] = useState("proposed");
  const [proposedClassConfigs, setProposedClassConfigs] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { run, stepIndex } = useSelector((state) => state.globalJoyride);

  useEffect(() => {
    if (!startClassTour) return;
    if (!proposedClassConfigs.length) return;

    dispatch(startTour({
      name: "class",
      steps: classSteps({
        dispatch,
        setActiveTab,
        navigate,
        stopTour: () => dispatch(stopTour()),
      }),
    }));

    // start from first step
    setTimeout(() => {
      dispatch(setStepIndex(0));
    }, 200);

  }, [startClassTour, proposedClassConfigs.length, dispatch, navigate]);

  // Memoized derived data
  const proposedClasses = useMemo(
    () => allClasses?.filter((cls) => (cls.status === 1 || cls.status === 2) && cls.is_proposed_class == 1),
    [allClasses]
  );

  const activeClasses = useMemo(
    () => allClasses?.filter((cls) => cls.status === 2),
    [allClasses]
  );

  // Stable section name generator
  // Modify generateSectionNames to accept existing names
  const generateSectionNames = useCallback((className, numberOfSections, existingNames = []) => {
    const sectionLabels = ["A", "B", "C", "D", "E", "F", "G", "H", "I", "J"];
    const newSectionNames = [];

    for (let i = 0; i < numberOfSections; i++) {
      if (existingNames[i]) {
        // If an existing name is available for this index, use it
        newSectionNames.push(existingNames[i]);
      } else {
        // Otherwise, generate a new name using the label
        const sectionLabel = sectionLabels[i];
        if (sectionLabel) { // Ensure we don't go out of bounds of sectionLabels
          newSectionNames.push(`${className} ${sectionLabel}`);
        } else {
          // Fallback if we run out of labels (shouldn't happen with current max 10)
          newSectionNames.push(`${className} Section ${i + 1}`);
        }
      }
    }
    return newSectionNames;
  }, []);
  // Initialize proposed class configs
  useEffect(() => {
    if (!proposedClasses.length) {
      setProposedClassConfigs([]);
      return;
    }

    const initialConfigs = proposedClasses.map((cls) => {
      let sectionNames;
      let localSectionTeachers = {};

      if (cls.sections_info?.length > 0) {
        // If sections_info exists, these are the authoritative names and teachers
        sectionNames = cls.sections_info.map((sec) => sec.section_name);
        cls.sections_info.forEach((sec, index) => {
          if (sec.class_teacher_id || sec.class_teacher_id === 0) {
            localSectionTeachers[`section-${index}`] = sec.class_teacher_id;
          }
        });
      } else {
        // If no sections_info, use or generate based on stored data or default
        // When initializing, there are no 'existingNames' from current state,
        // so we generate based on the proposed class's initial section count.
        // If cls.section_names exists from previous non-persisted state, use it as existing.
        sectionNames = generateSectionNames(cls.class_name, cls.sections || 1, cls.section_names || []);

        if (cls.local_section_teachers) {
          Object.assign(localSectionTeachers, cls.local_section_teachers);
        } else {
          // Initialize teachers to null if not present
          sectionNames.forEach((_, index) => {
            localSectionTeachers[`section-${index}`] = null;
          });
        }
      }

      const subjectPeriods = {};
      const selectedSubjects =
        cls.subjects?.map((s) => s.subject_id) || cls.selected_subjects || [];
      selectedSubjects.forEach((subjectId) => {
        const subjectInfo =
          cls.subjects?.find((s) => s.subject_id === subjectId) || {};
        subjectPeriods[subjectId] = {
          max_periods_per_week: subjectInfo.max_periods_per_week || 0,
          theoretical_classes: subjectInfo.theoretical_classes || 0,
          practical_classes: subjectInfo.practical_classes || 0,
        };
      });

      return {
        id: cls.class_id,
        name: cls.class_name,
        selected: cls.status === 2,
        sections: sectionNames.length, // Ensure sections count matches names
        selectedSubjects,
        sectionNames,
        localSectionTeachers,
        subjectPeriods,
        hasExistingSections: cls.sections_info?.length > 0,
        status: cls.status,
        sections_info: cls.sections_info || [],
      };
    });

    setProposedClassConfigs(initialConfigs);
  }, [proposedClasses, generateSectionNames]); // generateSectionNames is a dependency
  // Initial data loading
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        // First dispatch getMasterDataByUserId and wait until it's done
        await dispatch(getMasterDataByUserId(user.id)).unwrap();

        // Then dispatch the others sequentially
        await dispatch(fetchClasses()).unwrap();
        await dispatch(fetchSubjects()).unwrap();
        await dispatch(fetchTeachers()).unwrap();
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchAllData();
  }, [dispatch, user.id]);

  // useEffect(() => {
  //   if (!runClassTour) return;

  //   const stepSelectors = [
  //     ".add-class-step",
  //     ".add-class-name-step",
  //     ".add-class-teacher-step",
  //     ".add-class-subject-step",
  //     ".add-class-periods-step",
  //     ".add-class-submit-step",
  //   ];

  //   const selector = stepSelectors[classStepIndex];
  //   if (!selector) return;

  //   let attempts = 0;

  //   const interval = setInterval(() => {
  //     const el = document.querySelector(selector);

  //     if (el) {
  //       const rect = el.getBoundingClientRect();

  //       // SMART SCROLL:
  //       // Scroll ONLY if the element is not visible
  //       if (rect.top < 0 || rect.bottom > window.innerHeight) {
  //         el.scrollIntoView({ behavior: "smooth", block: "center" });
  //       }

  //       // Reposition Joyride spotlight
  //       window.dispatchEvent(new Event("resize"));
  //       clearInterval(interval);
  //     }

  //     attempts++;
  //     if (attempts > 20) clearInterval(interval);
  //   }, 150);

  //   return () => clearInterval(interval);
  // }, [runClassTour, classStepIndex]);




  console.log("Proposed Class Configs:", proposedClassConfigs);
  const handleSubjectToggle = useCallback(
    (classId, newSelectedSubjects, newSubjectPeriods = {}) => {
      setProposedClassConfigs((prevConfigs) =>
        prevConfigs.map((cls) =>
          cls.id === classId
            ? {
              ...cls,
              selectedSubjects: newSelectedSubjects,
              subjectPeriods: {
                ...cls.subjectPeriods,
                ...newSubjectPeriods,
              },
            }
            : cls
        )
      );
    },
    []
  );

  const handleProposedClassToggle = useCallback(async (classId) => {
    const targetClass = proposedClassConfigs.find((cls) => cls.id === classId);
    if (!targetClass) return;

    const isCurrentlySelected = targetClass.selected;
    // const confirmMsg = isCurrentlySelected
    //   ? "Do you want to deactivate this class?"
    //   : "Do you want to activate this class?";

    // const confirmed = window.confirm(confirmMsg);
    // if (!confirmed) return;

    const newStatus = isCurrentlySelected ? 1 : 2;

    // // Dispatch status update
    // await dispatch(manageClassStatusThunk({ classId, status: newStatus }));

    // Optional: Refresh data after status update
    // await dispatch(getActiveClassesThunk());

    // Update local state toggle
    setProposedClassConfigs((prevConfigs) =>
      prevConfigs.map((cls) =>
        cls.id === classId ? { ...cls, selected: !cls.selected, status: newStatus } : cls
      )
    );
  }, [dispatch, proposedClassConfigs]);



  const handleSectionsChange = useCallback(
    (classId, numberOfSections) => {
      setProposedClassConfigs((prevConfigs) =>
        prevConfigs.map((cls) => {
          if (cls.id === classId) {
            // Pass existing section names to the generator
            const newSectionNames = generateSectionNames(
              cls.name,
              numberOfSections,
              cls.sectionNames // Pass the current sectionNames from the config
            );
            const newLocalSectionTeachers = {};

            // Preserve existing teacher assignments where possible
            for (let i = 0; i < numberOfSections; i++) {
              const sectionKey = `section-${i}`;
              if (cls.localSectionTeachers?.[sectionKey]) {
                newLocalSectionTeachers[sectionKey] = cls.localSectionTeachers[sectionKey];
              }
            }

            return {
              ...cls,
              sections: numberOfSections,
              sectionNames: newSectionNames,
              localSectionTeachers: newLocalSectionTeachers,
              hasExistingSections: cls.sections_info?.length > 0 && numberOfSections <= cls.sections_info.length,
            };
          }
          return cls;
        })
      );
    },
    [generateSectionNames]
  );

  const handleTeacherChange = useCallback(
    (classId, sectionIndex, teacherId) => {
      setProposedClassConfigs((prevConfigs) =>
        prevConfigs.map((cls) =>
          cls.id === classId
            ? {
              ...cls,
              localSectionTeachers: {
                ...cls.localSectionTeachers,
                [`section-${sectionIndex}`]: teacherId,
              },
            }
            : cls
        )
      );
    },
    []
  );

  const handleSaveProposedClasses = async () => {
    if (!schoolId || !user?.id) {
      alert("School ID or User ID is missing.");
      return;
    }

    // Filter only selected classes
    const selectedClasses = proposedClassConfigs.filter(cls => cls.selected);

    if (selectedClasses.length === 0) {
      alert("Please select at least one class to process.");
      return;
    }

    //Validation: Check if any selected section lacks a class teacher
    for (const cls of selectedClasses) {
      for (let i = 0; i < cls.sectionNames.length; i++) {
        const teacherId = cls.localSectionTeachers?.[`section-${i}`];
        if (!teacherId) {
          alert(`Please assign a class teacher for section "${cls.sectionNames[i]}"`);
          return;
        }
      }
    }

    try {
      setIsProcessing(true);

      // Build activationData only from selected classes
      const activationData = proposedClassConfigs.map((cls) => {
        const sections = cls.sectionNames.map((name, index) => ({
          section_name: name,
          class_teacher_id: cls.localSectionTeachers[`section-${index}`],
        }));

        return {
          class_id: cls.id,
          subjects: cls.selectedSubjects.map((subjectId) => ({
            subject_id: subjectId,
            ...cls.subjectPeriods[subjectId],
          })),
          sections,
          hasExistingSections: cls.hasExistingSections,
          status: cls.status,
        };
      });

      await dispatch(
        activateClassesWithSubjectsAndSections({
          school_id: schoolId,
          user_id: user.id,
          classes: activationData,
        })
      ).unwrap();

      alert("Classes processed successfully!");

      if (run) {
        setTimeout(() => {
          dispatch(setStepIndex(11));
          // ✅ move to Add Class
          window.dispatchEvent(new Event("resize"));
        }, 300);
      }

      dispatch(fetchClasses());
      setActiveTab("list");
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message || "Something went wrong."}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNewClassSubmit = async (newClassesData) => {
    if (!schoolId || !user?.id) {
      alert("School ID or User ID is missing.");
      return;
    }

    if (!Array.isArray(newClassesData) || newClassesData.length === 0) {
      alert("No class data to submit.");
      return;
    }

    // Duplicate class name check
    const existingClassNames = allClasses.map(cls => cls.class_name?.trim().toLowerCase());
    const duplicateNames = newClassesData
      .map(c => c.class_name?.trim().toLowerCase())
      .filter(name => existingClassNames.includes(name));

    if (duplicateNames.length > 0) {
      alert(`The following class(es) already exist: ${duplicateNames.join(", ")}`);
      return;
    }

    setIsProcessing(true);
    try {
      const creationPromises = newClassesData.map((classData) => {
        const subjectPeriods = {};
        classData.selected_subjects.forEach((sub) => {
          subjectPeriods[sub.subject_id] = {
            max_periods_per_week: sub.max_periods_per_week,
            theoretical_classes: sub.theoretical_classes,
            practical_classes: sub.practical_classes,
          };
        });

        const sections = classData.sectionNames.map((sectionName, index) => ({
          section_name: sectionName,
          class_teacher_id: classData.sectionTeachers?.[`section-${index}`] || null,
        }));

        const payload = {
          class_name: classData.class_name,
          school_id: schoolId,
          user_id: user.id,
          status: 1,
          sections,
          selected_subjects: classData.selected_subjects.map((s) => s.subject_id),
          subject_periods: subjectPeriods,
        };

        return dispatch(createClass(payload)).unwrap();
      });

      const results = await Promise.all(creationPromises);

      alert(`${results.length} class(es) proposed successfully!`);

      dispatch(fetchClasses());
      setActiveTab("proposed");


      if (run) {
        setTimeout(() => {
          setActiveTab("proposed");

          setTimeout(() => {
            const btn = document.querySelector(".save-config-step");

            if (btn) {
              btn.scrollIntoView({ behavior: "smooth", block: "center" });

              // Wait for scroll animation to finish
              // setTimeout(() => {
              //   setClassStepIndex(6);
              //   window.dispatchEvent(new Event("resize")); // Recalculate spotlight
              // }, 600); // <-- delay is IMPORTANT
            }
          }, 300);
        }, 300);
      }



    } catch (error) {
      console.error("Error adding new classes:", error);
      alert(`Error: ${error.message || "Failed to add one or more classes."}`);
    } finally {
      setIsProcessing(false);
    }
  };



  const handleDeleteClass = async (id) => {
    if (window.confirm("Are you sure you want to delete this class?")) {
      try {
        setIsProcessing(true);
        await dispatch(deleteClass(id)).unwrap();
        dispatch(fetchClasses());
      } catch (error) {
        console.error("Delete error:", error);
        alert(`Error: ${error.message || "Failed to delete class."}`);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const isLoading =
    classesLoading || subjectsLoading || teachersLoading || isProcessing;

  return (
    <>
      <div id="class-confirm-anchor" />
      {/* <GlobalJoyrideCloseButton /> */}
      <div className=" mt-2 px-5 class-management-wrapper">
        <div className="px-2">
          {/* <Row className="mb-3">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="text-primary fw-bold mb-0">Class Management</h2>
          </div>
        </Col>
      </Row> */}

          {classesError && <Alert variant="danger">{classesError}</Alert>}

          <Row className="mb-3">
            <Col>
              <div className="d-flex flex-wrap gap-2">
                <Button
                  className="class-list-step"
                  variant={activeTab === "proposed" ? "primary" : "outline-primary btn-sm"}
                  onClick={() => {
                    setActiveTab("proposed");
                    dispatch(setStepIndex(1));   // 👈 move joyride to step index 1
                  }}
                  disabled={isLoading}
                >
                  <Check size={16} className="me-2" /> Select Classes
                </Button>
                <Button
                  variant={activeTab === "list" ? "primary" : "outline-primary btn-sm"}
                  onClick={() => setActiveTab("list")}
                  disabled={isLoading}
                  className="class-list-view-step"
                >
                  <Users size={16} className="me-2" /> Class List (
                  {activeClasses.length})
                </Button>
                <Button
                  variant={activeTab === "add" ? "primary" : "outline-primary btn-sm"}
                  className="add-class-step"
                  onClick={() => setActiveTab("add")}
                  disabled={isLoading}
                >
                  <Plus size={16} className="me-2" /> Add Class
                </Button>

              </div>
            </Col>
          </Row>

          <div>
            <ErrorBoundary className={`${run ? "disable-clicks" : ""}`}>
              {activeTab === "proposed" && (
                <ProposedClassesTab
                  proposedClasses={proposedClassConfigs}
                  subjects={subjects}
                  teachers={teachers}
                  loading={isLoading}
                  handleProposedClassToggle={handleProposedClassToggle}
                  handleSectionsChange={handleSectionsChange}
                  handleSubjectToggle={handleSubjectToggle}
                  handleSaveProposedClasses={handleSaveProposedClasses}
                  handleTeacherChange={handleTeacherChange}
                // setClassStepIndex={setClassStepIndex}
                />
              )}

              {activeTab === "list" && (
                <ClassListTab
                  classes={activeClasses}
                  loading={isLoading}
                  handleDeleteClass={handleDeleteClass}
                />
              )}

              {activeTab === "add" && (
                <AddClassTab
                  subjects={subjects}
                  teachers={teachers}
                  loading={isLoading}
                  handleNewClassSubmit={handleNewClassSubmit}
                  setActiveTab={setActiveTab}
                />
              )}
            </ErrorBoundary>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClassManagement;